import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MwOrderLinesApiService } from 'app/services/order-lines-api.service';
import * as orderLinesActions from '../actions/order-lines.actions';
import * as notifyActions from 'app/store/general/actions/notify.actions';

@Injectable()
export class OrderLinesEffects {
  loadOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.loadOrderLines),
      switchMap(({ loadOptions }) =>
        this.orderLinesApiService.getOrderLines(loadOptions).pipe(
          map((response) =>
            orderLinesActions.loadOrderLinesSuccess({ payload: response })
          ),
          catchError((error) =>
            of(orderLinesActions.loadOrderLinesFailed({ error }))
          )
        )
      )
    )
  );

  createOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.createOrderLine),
      switchMap(({ orderLine }) =>
        this.orderLinesApiService.createOrderLine(orderLine).pipe(
          switchMap(() => [
            orderLinesActions.createOrderLineSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.add_order_line_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(orderLinesActions.createOrderLineFailed({ error }))
          )
        )
      )
    )
  );

  updateOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.updateOrderLine),
      switchMap(({ orderLine }) =>
        this.orderLinesApiService.updateOrderLine(orderLine).pipe(
          switchMap(() => [
            orderLinesActions.updateOrderLineSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.edit_order_line_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(orderLinesActions.updateOrderLineFailed({ error }))
          )
        )
      )
    )
  );

  deleteOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.deleteOrderLine),
      switchMap(({ orderLine }) =>
        this.orderLinesApiService.deleteOrderLine(orderLine).pipe(
          switchMap(() => [
            orderLinesActions.deleteOrderLineSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.delete_order_line_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(orderLinesActions.deleteOrderLineFailed({ error }))
          )
        )
      )
    )
  );

  deleteOrderLinesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.deleteOrderLinesList),
      switchMap(({ orderLines }) =>
        this.orderLinesApiService.deleteOrderLinesList(orderLines).pipe(
          switchMap(() => [
            orderLinesActions.deleteOrderLinesListSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.delete_order_lines_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(orderLinesActions.deleteOrderLinesListFailed({ error }))
          )
        )
      )
    )
  );

  createOrderLineAssignments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(orderLinesActions.createOrderLineAssignments),
      switchMap(({ orderLine, quantity }) =>
        this.orderLinesApiService
          .generateOrderLinesAssignments(orderLine, quantity)
          .pipe(
            switchMap(() => [
              orderLinesActions.createOrderLineAssignmentsSuccess(),
              notifyActions.notifySuccess({
                notification: {
                  phrase:
                    quantity > 1
                      ? 'OPERATION.generate_assignments_successful'
                      : 'OPERATION.generate_assignment_successful',
                },
              }),
            ]),
            catchError((error) =>
              of(orderLinesActions.createOrderLineAssignmentsFailed({ error }))
            )
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private orderLinesApiService: MwOrderLinesApiService
  ) {}
}
